import { z } from 'zod';

import { entityIdSchema, isoDateTimeSchema, pageMetaSchema, revisionSchema } from './common.js';

export const maximumDocumentBytes = 1_572_864;

export const documentPurposeSchema = z.enum(['attachment', 'child_justification', 'gift_voucher']);

export const documentLinkTypeSchema = z.enum([
  'customer',
  'reservation',
  'reservation_participant',
  'gift_voucher',
]);

export const documentMimeTypeSchema = z.enum([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
]);

export const justificationStatusSchema = z.enum([
  'missing',
  'pending_review',
  'validated',
  'refused',
  'legacy_reported',
  'archived',
]);

export const documentTypeSchema = z.enum([
  'invoice',
  'quote',
  'deposit_contract',
  'gift_voucher',
  'stay_sheet',
  'attachment',
  'child_justification',
]);

const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const safeFilenameSchema = z.string().trim().min(1).max(180);
const idempotencyKeySchema = z.string().trim().min(8).max(200);

export const documentLinkSchema = z
  .object({
    id: entityIdSchema,
    type: documentLinkTypeSchema,
    customerId: entityIdSchema.nullable(),
    reservationId: entityIdSchema.nullable(),
    participantId: entityIdSchema.nullable(),
    giftVoucherId: entityIdSchema.nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
  })
  .strict();

export const documentVersionSchema = z
  .object({
    id: entityIdSchema,
    documentId: entityIdSchema,
    versionNumber: z.number().int().positive(),
    filename: safeFilenameSchema,
    mimeType: documentMimeTypeSchema,
    sizeBytes: z.number().int().min(1).max(maximumDocumentBytes),
    sha256: sha256Schema,
    etag: z.string().min(66).max(70),
    templateId: entityIdSchema.nullable(),
    templateVersion: z.string().trim().min(1).max(80).nullable(),
    createdAt: isoDateTimeSchema,
    createdBy: entityIdSchema.nullable(),
  })
  .strict();

export const documentSchema = z
  .object({
    id: entityIdSchema,
    purpose: documentPurposeSchema,
    revision: z.number().int().positive(),
    activeVersion: documentVersionSchema.nullable(),
    links: z.array(documentLinkSchema),
    archivedAt: isoDateTimeSchema.nullable(),
    archivedBy: entityIdSchema.nullable(),
    createdAt: isoDateTimeSchema,
    createdBy: entityIdSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: entityIdSchema.nullable(),
  })
  .strict();

export const documentPageSchema = z
  .object({ items: z.array(documentSchema), page: pageMetaSchema })
  .strict();

export const documentListQuerySchema = z
  .object({
    cursor: entityIdSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();

export const documentUploadMetadataSchema = z
  .object({
    filename: safeFilenameSchema,
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const documentArchiveInputSchema = z
  .object({
    revision: z.number().int().positive(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const justificationReviewInputSchema = z
  .object({
    decision: z.enum(['validated', 'refused']),
    reason: z.string().trim().max(1_000).default(''),
    revision: z.number().int().positive(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.decision === 'refused' && value.reason.length === 0) {
      context.addIssue({
        code: 'custom',
        path: ['reason'],
        message: 'Un motif est obligatoire pour refuser un justificatif.',
      });
    }
  });

export const participantJustificationSchema = z
  .object({
    id: entityIdSchema.nullable(),
    reservationId: entityIdSchema,
    participantId: entityIdSchema,
    required: z.boolean(),
    status: justificationStatusSchema,
    document: documentSchema.nullable(),
    reviewReason: z.string(),
    reviewedAt: isoDateTimeSchema.nullable(),
    reviewedBy: entityIdSchema.nullable(),
    revision: revisionSchema,
    legacyReplacementRequired: z.boolean(),
  })
  .strict();

export const participantJustificationListSchema = z
  .object({ items: z.array(participantJustificationSchema) })
  .strict();

export const giftVoucherDocumentGenerateInputSchema = z
  .object({
    voucherRevision: z.number().int().positive(),
    templateId: entityIdSchema.optional(),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const giftVoucherDocumentSnapshotSchema = z
  .object({
    reference: z.string().trim().min(1).max(120),
    type: z.enum(['amount', 'stay']),
    valueCents: z.number().int().nonnegative(),
    balanceCents: z.number().int().nonnegative(),
    stay: z
      .object({
        id: entityIdSchema,
        reference: z.string().trim().min(1).max(120),
        name: z.string().trim().min(1).max(240),
      })
      .strict()
      .nullable(),
    purchaser: z.string().trim().min(1).max(240),
    beneficiary: z.string().trim().min(1).max(240),
    validFrom: z.iso.date(),
    validUntil: z.iso.date(),
    message: z.string().max(2_000),
    templateId: entityIdSchema,
    templateVersion: z.string().trim().min(1).max(80),
  })
  .strict();

export const giftVoucherDocumentEmailInputSchema = z
  .object({
    recipient: z.string().trim().toLowerCase().pipe(z.email()),
    idempotencyKey: idempotencyKeySchema,
  })
  .strict();

export const documentOutboxResultSchema = z
  .object({
    outboxId: entityIdSchema,
    documentId: entityIdSchema,
    status: z.enum(['queued', 'delivery_disabled']),
    code: z.enum(['EMAIL_QUEUED', 'EMAIL_DELIVERY_DISABLED']),
  })
  .strict();

export const documentDownloadMetadataSchema = z
  .object({
    documentId: entityIdSchema,
    versionId: entityIdSchema,
    filename: safeFilenameSchema,
    mimeType: documentMimeTypeSchema,
    sizeBytes: z.number().int().min(1).max(maximumDocumentBytes),
    sha256: sha256Schema,
    etag: z.string().min(66).max(70),
  })
  .strict();

export type Document = z.infer<typeof documentSchema>;
export type DocumentArchiveInput = z.infer<typeof documentArchiveInputSchema>;
export type DocumentDownloadMetadata = z.infer<typeof documentDownloadMetadataSchema>;
export type DocumentLink = z.infer<typeof documentLinkSchema>;
export type DocumentLinkType = z.infer<typeof documentLinkTypeSchema>;
export type DocumentMimeType = z.infer<typeof documentMimeTypeSchema>;
export type DocumentOutboxResult = z.infer<typeof documentOutboxResultSchema>;
export type DocumentPage = z.infer<typeof documentPageSchema>;
export type DocumentPurpose = z.infer<typeof documentPurposeSchema>;
export type DocumentType = z.infer<typeof documentTypeSchema>;
export type DocumentUploadMetadata = z.infer<typeof documentUploadMetadataSchema>;
export type DocumentVersion = z.infer<typeof documentVersionSchema>;
export type GiftVoucherDocumentEmailInput = z.infer<typeof giftVoucherDocumentEmailInputSchema>;
export type GiftVoucherDocumentGenerateInput = z.infer<
  typeof giftVoucherDocumentGenerateInputSchema
>;
export type GiftVoucherDocumentSnapshot = z.infer<typeof giftVoucherDocumentSnapshotSchema>;
export type JustificationReviewInput = z.infer<typeof justificationReviewInputSchema>;
export type JustificationStatus = z.infer<typeof justificationStatusSchema>;
export type ParticipantJustification = z.infer<typeof participantJustificationSchema>;
